import { useState, useCallback } from 'react';

/**
 * Custom hook for unified snackbar/notification management.
 * Replaces multiple separate snackbar states with a single, flexible system.
 *
 * Usage:
 * const { snackbar, showSnackbar, hideSnackbar } = useSnackbar();
 *
 * // Show a success message
 * showSnackbar('Operation completed', 'success');
 *
 * // Show an error message
 * showSnackbar('Something went wrong', 'error');
 *
 * // In JSX:
 * <Snackbar open={snackbar.open} onClose={hideSnackbar} autoHideDuration={snackbar.duration}>
 *   <Alert severity={snackbar.severity}>{snackbar.message}</Alert>
 * </Snackbar>
 */
export const useSnackbar = (defaultDuration = 3000) => {
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success', // 'success' | 'error' | 'warning' | 'info'
    duration: defaultDuration,
  });

  const showSnackbar = useCallback((message, severity = 'success', duration = null) => {
    setSnackbar({
      open: true,
      message,
      severity,
      duration: duration ?? defaultDuration,
    });
  }, [defaultDuration]);

  const hideSnackbar = useCallback((event, reason) => {
    // Don't close on clickaway
    if (reason === 'clickaway') {
      return;
    }
    setSnackbar(prev => ({ ...prev, open: false }));
  }, []);

  // Convenience methods for common scenarios
  const showSuccess = useCallback((message, duration = null) => {
    showSnackbar(message, 'success', duration);
  }, [showSnackbar]);

  const showError = useCallback((message, duration = null) => {
    showSnackbar(message, 'error', duration);
  }, [showSnackbar]);

  const showWarning = useCallback((message, duration = null) => {
    showSnackbar(message, 'warning', duration);
  }, [showSnackbar]);

  const showInfo = useCallback((message, duration = null) => {
    showSnackbar(message, 'info', duration);
  }, [showSnackbar]);

  return {
    snackbar,
    showSnackbar,
    hideSnackbar,
    showSuccess,
    showError,
    showWarning,
    showInfo,
  };
};

export default useSnackbar;
